import { useHomepageSection } from '@/hooks/useHomepageSection';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { ABOUT_COPY, SITE } from '@/content/yawasCopy';
import { DEFAULT_RESTAURANT_HOURS, parseOpeningHours } from '@/content/openingHours';
import OpeningHoursList from '@/components/feature/OpeningHoursList';

const AboutSection = () => {
  const { data } = useHomepageSection('about');
  const { get } = useSiteSettings();
  const address = get('restaurant_address', SITE.addressShort) || SITE.addressShort;
  const addressDisplay = address.includes('Schuppis')
    ? SITE.addressLines.join('\n')
    : address;
  const openingHours = parseOpeningHours(get('restaurant_hours', DEFAULT_RESTAURANT_HOURS));


  const title = data?.title?.trim() || ABOUT_COPY.title;
  const subtitle = data?.subtitle?.trim() || ABOUT_COPY.subtitle;
  const badgeText = data?.badge_text?.trim() || ABOUT_COPY.badge;
  const imageUrl = data?.image_url || '/food/smash-burger-classic.png';
  const imageUrl2 = data?.image_url2 || '/food/smash-burger-150.png';

  const paragraphs =
    data?.description?.trim() || data?.description2?.trim()
      ? [data.description, data.description2]
          .filter((block): block is string => Boolean(block?.trim()))
          .flatMap((block) =>
            block
              .split(/\n\n+/)
              .map((s) => s.trim())
              .filter(Boolean),
          )
      : ABOUT_COPY.paragraphs;

  return (
    <section id="about" className="py-20 md:py-28 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Images */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden">
                <img
                  src={imageUrl}
                  alt="Hausgemachter Smash Burger bei Yawas Kebab in St. Gallen"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden mt-8">
                <img
                  src={imageUrl2}
                  alt="Frisch zubereiteter Burger mit Pommes – Yawas Kebab"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap">
              {badgeText}
            </div>
          </div>

          {/* Content */}
          <div className="pt-8 lg:pt-0">
            <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">{title}</span>
            <h2
              className="text-3xl md:text-4xl font-bold text-stone-900 mt-3 mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {subtitle}
            </h2>
            <div className="space-y-4 mb-8">
              {paragraphs.map((p) => (
                <p key={p.slice(0, 40)} className="text-stone-600 text-base leading-relaxed">
                  {p}
                </p>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {ABOUT_COPY.highlights.map((val) => (
                <div key={val.label} className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                    <i className={`${val.icon} text-amber-600`}></i>
                  </div>
                  <span className="text-stone-700 text-sm font-medium">{val.label}</span>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-6 border border-stone-100">
              <h3 className="text-stone-900 font-semibold text-sm mb-4 flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-amber-500"></i>
                </div>
                Öffnungszeiten
              </h3>
              <div className="space-y-2">
                <OpeningHoursList rows={openingHours} />
              </div>
              <div className="mt-4 pt-4 border-t border-stone-100 flex items-start gap-2 text-sm text-stone-500">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-amber-500"></i>
                </div>
                <span className="whitespace-pre-line">{addressDisplay}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
